import { SliceComponentProps } from "@prismicio/react";
import _get from "lodash/get";
import { RegisterOptions, useFormContext } from "react-hook-form";

import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import MultiLineText from "components/form/multiLineText/MultiLineText";
import { MultiLineTextFieldSlice } from "prismicio-types";
import { useGetProduct } from "queries/legacy/product-queries";
import { FieldSliceContext, MultiLineTextSchemaField } from "types/schema";
import {
  prismicFieldConfigurationError,
  SSR_CONFIGURATION_ERROR,
} from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";
import {
  getInputPropsFromSchemaRules,
  isFieldType,
  useFindByKey,
} from "utils/schema-utils";

import styles from "../Fields.module.scss";

export default function MultiLineTextField({
  slice,
}: SliceComponentProps<MultiLineTextFieldSlice, FieldSliceContext>) {
  const {
    formState: { errors },
    register,
  } = useFormContext();

  const { data: product } = useGetProduct();

  const findByKey = useFindByKey();
  const interpolate = useInterpolate();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const schemaKey = slice.primary.muse_schema_key;

  if (!schemaKey) {
    throw new Error("Missing Muse Schema Key");
  }

  const field = findByKey(schemaKey);

  if (!field) {
    return null;
  }

  if (!isFieldType<MultiLineTextSchemaField>(field, "multi_line_text")) {
    throw prismicFieldConfigurationError(
      schemaKey,
      "multi_line_text",
      field.metas[0].canvasComponent
    );
  }

  const { maxLength, minLength } = getInputPropsFromSchemaRules(field.rules);

  // Get validation options to spread into the `react-hook-form` `Controller` rules
  const registerOptions: RegisterOptions = {
    ...(maxLength && {
      maxLength: {
        value: maxLength,
        message: interpolate(
          product.content.canvas.form_field_max_length_error_message ||
            CANVAS_FALLBACK_CONTENT.form_field_max_length_error_message
        ),
      },
    }),
    ...(minLength && {
      minLength: {
        value: minLength,
        message: interpolate(
          product.content.canvas.form_field_min_length_error_message ||
            CANVAS_FALLBACK_CONTENT.form_field_min_length_error_message
        ),
      },
    }),
    required:
      field.flags?.presence === "required" &&
      interpolate(
        product.content.canvas.form_field_required_error_message ||
          CANVAS_FALLBACK_CONTENT.form_field_required_error_message
      ),
  };

  const error = errors && _get(errors, field.urlKey)?.message;

  return (
    <div className={styles.field}>
      {slice.primary.label && (
        <label className={styles.label} htmlFor={field.urlKey}>
          {interpolate(slice.primary.label)}
        </label>
      )}

      <ErrorBoundary>
        <MultiLineText
          aria-invalid={!!error}
          id={field.urlKey}
          maxLength={maxLength}
          placeholder={interpolate(slice.primary.placeholder || "")}
          {...register(field.urlKey, registerOptions)}
        />
      </ErrorBoundary>

      {error && <p className={styles.error}>{error as string}</p>}
    </div>
  );
}
